// src/pages/cotizaciones/Cotizaciones.tsx
import React, { useState, useEffect, useContext } from "react";
import { toast } from '../../utils/toast';
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import PageLayout from "../../components/layout/PageLayout";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import { BuscadorClientes } from "../../components/module/ventas/BuscadorClientes";
import { BuscadorArticulos } from "../../components/module/ventas/BuscadorArticulos";
import { CarritoVenta } from "../../components/module/ventas/CarritoVenta";
import { ResumenVenta } from "../../components/module/ventas/ResumenVenta";
import { useAuth } from "../../context/AuthContext";
import Swal from "sweetalert2";
import { CotizacionesApi } from "../../services/api/CotizacionesApi";
import axios from "axios";
import { DarkModeContext } from "../../context/darkModeContext";
interface CotizacionState {
  id?: number;
  cliente?:
    | {
        id: number;
        nombre: string;
      }
    | undefined;
  articulos: Array<any>;
  subtotal: number;
  descuentoTotal: number;
  impuestos: Array<{
    nombre: string;
    valor: number;
  }>;
  totalAPagar: number;
  codigoCotizacion?: string;
}
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4020';
const Cotizaciones = () => {
  const { user } = useAuth();
  const [cotizacionActual, setCotizacionActual] = useState<CotizacionState>({
    articulos: [],
    subtotal: 0,
    descuentoTotal: 0,
    impuestos: [],
    totalAPagar: 0,
  });
  const [cotizacionIniciada, setCotizacionIniciada] = useState(false);
  const [sucursales, setSucursales] = useState<Array<any>>([]);
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState<string>("");
  const [loadingSucursales, setLoadingSucursales] = useState(true);
  const [mostrarFinalizar, setMostrarFinalizar] = useState(false);
  const { darkMode } = useContext(DarkModeContext);
  useEffect(() => {
    const inicializarComponente = async () => {
      try {
        setLoadingSucursales(true);
        const { data: sucursalesData } = await axios.post(
          `${API_URL}/administracion/sucursales/findAll`
        );
        setSucursales(sucursalesData);
        if (sucursalesData && sucursalesData.length > 0) {
          setSucursalSeleccionada(sucursalesData[0].Id.toString());
        }
      } catch (error) {
        console.error("Error en inicialización:", error);
        toast.error("Error al inicializar el componente");
      } finally {
        setLoadingSucursales(false);
      }
    };
    inicializarComponente();
  }, []);
  const handleClienteSeleccionado = async (cliente: {
    id: number;
    nombre: string;
  }) => {
    if (!cotizacionIniciada && sucursalSeleccionada) {
      try {
        const data = await CotizacionesApi.crearCotizacion(
          cliente.id,
          user?.Id || 0,
          new Date().toISOString()
        );
        setCotizacionActual((prev) => ({
          ...prev,
          id: data.id,
          codigoCotizacion: data.codigoCotizacion,
          cliente: cliente,
        }));
        setCotizacionIniciada(true);
        toast.success("Puede comenzar a agregar artículos");
      } catch (error) {
        toast.error("Error");
      }
    } else if (!sucursalSeleccionada) {
      toast.warning("Debe seleccionar una sucursal antes de iniciar la cotización");
    }
  };
  // src/pages/cotizaciones/Cotizaciones.tsx (continuación)
  const handleArticuloSeleccionado = async (
    articulo: any,
    cantidad: number
  ) => {
    if (!cotizacionActual.id) {
      toast.error("No hay una cotización activa para agregar artículos");
      return;
    }
    try {
      const response = await CotizacionesApi.ajustarArticulos({
        idCotizacion: cotizacionActual.id,
        idArticulo: articulo.Id,
        cantidad,
        accion: "AddAll",
      });
      // Asegurar que la respuesta tenga el formato esperado
      const responseData = {
        articulos: response.articulos,
        subtotal: response.subtotal,
        descuentoTotal: response.descuentoTotal,
        impuestos: response.impuestos,
        totalAPagar: response.totalAPagar,
      };
      handleArticulosUpdated(responseData);
      toast.success(`Se agregó ${cantidad} unidad(es) de ${articulo.Nombre}`);
    } catch (error) {
      console.error("Error al agregar artículo:", error);
      toast.error("Error");
    }
  };
  const handleArticulosUpdated = (response: any) => {
    if (!response || !response.articulos) {
      console.error("Respuesta inválida del servidor");
      return;
    }
    setCotizacionActual((prev) => ({
      ...prev,
      articulos: response.articulos,
      subtotal: response.subtotal,
      descuentoTotal: response.descuentoTotal,
      impuestos: response.impuestos,
      totalAPagar: response.totalAPagar,
    }));
    if (response.articulos.length === 0) {
      toast.info("No hay artículos en la cotización");
    }
  };
  const handleFinalizarCotizacion = async () => {
    try {
      const result = await CotizacionesApi.finalizarCotizacion({
        idCotizacion: cotizacionActual.id || 0,
        idSucursal: Number(sucursalSeleccionada),
      });
      toast.success(`Cotización ${result.codigoCotizacion} generada exitosamente`);
      // Reiniciar estado
      setCotizacionActual({
        articulos: [],
        subtotal: 0,
        descuentoTotal: 0,
        impuestos: [],
        totalAPagar: 0,
      });
      setCotizacionIniciada(false);
      setMostrarFinalizar(false);
    } catch (error) {
      toast.error("Error");
    }
  };
  const handleCancelarCotizacion = async () => {
    try {
      const willCancel = await Swal.fire({
        title: "¿Cancelar Cotización?",
        text: "Esta acción no se puede deshacer",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Sí, cancelar",
        cancelButtonText: "No, mantener",
        reverseButtons: true,
      });
      if (willCancel.isConfirmed) {
        await CotizacionesApi.cancelarCotizacion(cotizacionActual.id || 0);
        setCotizacionActual({
          articulos: [],
          subtotal: 0,
          descuentoTotal: 0,
          impuestos: [],
          totalAPagar: 0,
        });
        setCotizacionIniciada(false);
        toast.success("Cotización Cancelada");
      }
    } catch (error) {
      toast.error("Error");
    }
  };
 return (
  <PageLayout icon={RequestQuoteIcon} title="Nueva Cotización">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ minWidth: 120, mb: 2 }}>
            <FormControl
              fullWidth
              disabled={cotizacionIniciada || loadingSucursales}
            >
              <InputLabel id="sucursal-select-label">Sucursal</InputLabel>
              <Select
                labelId="sucursal-select-label"
                id="sucursal-select"
                value={sucursalSeleccionada}
                label="Sucursal"
                onChange={(e) => setSucursalSeleccionada(e.target.value)}
              >
                {sucursales.map((sucursal) => (
                  <MenuItem
                    key={sucursal.Id}
                    value={sucursal.Id.toString()}
                  >
                    {sucursal.Nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>
        <Grid item xs={12} md={8}>
          {!cotizacionIniciada && (
            <BuscadorClientes
              onClienteSeleccionado={handleClienteSeleccionado}
              disabled={
                cotizacionIniciada ||
                !sucursalSeleccionada ||
                loadingSucursales
              }
            />
          )}
          {cotizacionIniciada && (
            <>
              <BuscadorArticulos
                onArticuloSeleccionado={handleArticuloSeleccionado}
                disabled={!cotizacionIniciada}
                idSucursal={Number(sucursalSeleccionada)}
                esCotizacion={true}
              />
              <CarritoVenta
                idVenta={cotizacionActual.id || 0}
                articulos={cotizacionActual.articulos}
                onStockUpdated={handleArticulosUpdated}
                showingPayment={mostrarFinalizar}
                esCotizacion={true}
              />
            </>
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          <ResumenVenta
            venta={{
              id: cotizacionActual.id || 0,
              cliente: cotizacionActual.cliente,
              numeroFactura: cotizacionActual.codigoCotizacion,
              subtotal: cotizacionActual.subtotal,
              descuentoTotal: cotizacionActual.descuentoTotal,
              impuestos: cotizacionActual.impuestos,
              totalAPagar: cotizacionActual.totalAPagar,
            }}
            onProcesarPago={handleFinalizarCotizacion}
            onCancelarVenta={handleCancelarCotizacion}
            disabled={
              !cotizacionIniciada || cotizacionActual.articulos.length === 0
            }
            showingPayment={mostrarFinalizar}
            esCotizacion={true}
          />
        </Grid>
      </Grid>
    </PageLayout>
);
};
export default Cotizaciones;
